import type { ReactElement } from 'react';
import { useEffect, useRef } from 'react';

import { useLocation } from 'react-router';
import { FeatureFlag } from '@/services/feature-flags';
import { useFlag, useFlagPayload } from '@/services/feature-flags/react';

import 'react-hubspot-tracking-code-hook';
import { HUBSPOT_TRACKING_ID } from '@/config/env';
import { useUserDetails } from '@/features/_shared/user';
import { hubspotInit } from '@/features/tracking/lib/hubspotInit';

const isTestEnv = import.meta.env.MODE === 'test' || import.meta.env.VITEST;

type PageViewEventsFilters = {
  allowed_path_patterns?: string;
};

export function HubspotTrackingLister({ children }: { readonly children: ReactElement }) {
  const initialized = useRef(false);
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);
  const isPageViewEventsEnabled = useFlag(FeatureFlag.HubspotPageviewEventsEnabled);
  const pageViewEventsFilters = useFlagPayload(FeatureFlag.HubspotPageviewEventsEnabled) as
    | PageViewEventsFilters
    | null
    | undefined;

  const { userEmail } = useUserDetails();
  const sendPageView = (page: string) => {
    // Check if page title is 'GX Go' - if so, don't send pageview event
    if (document.title === 'GX Go') {
      return;
    }

    if (pageViewEventsFilters?.allowed_path_patterns) {
      const filterRegex = new RegExp(pageViewEventsFilters.allowed_path_patterns);
      if (filterRegex.test(page)) {
        const _hsq = (window._hsq = window._hsq || []);
        _hsq.push(['setPath', page]);
        _hsq.push(['trackPageView']);
      }
    }
  };

  useEffect(() => {
    // Check if isPageViewEventsEnabled, otherwise do not initialize
    if (!isPageViewEventsEnabled) {
      return;
    }

    if (HUBSPOT_TRACKING_ID) {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.id = 'hs-script-loader';
      script.async = true;
      script.defer = true;
      const hubspotScriptUrl = `//js.hs-scripts.com/${HUBSPOT_TRACKING_ID}.js`;
      if (isTestEnv) {
        // Happy DOM blocks loading external scripts in tests.
        script.dataset.src = hubspotScriptUrl;
      } else {
        script.src = hubspotScriptUrl;
      }
      document.body.appendChild(script);

      if (userEmail) {
        hubspotInit(window.location.pathname, userEmail);
        initialized.current = true;
      }
    }
  }, [userEmail, isPageViewEventsEnabled]);

  // Track page views when location changes
  useEffect(() => {
    if (!initialized.current) {
      return;
    }

    // Check if this is a new path (equivalent of PUSH)
    if (location.pathname !== prevPathRef.current) {
      sendPageView(location.pathname);
      prevPathRef.current = location.pathname;
    }
  }, [location, pageViewEventsFilters]);

  return children;
}
